import { Modal } from "antd";
import classNames from "classnames";
import { ReactNode, useState } from "react";

import VLButton from "views/uikit/Buttons/VLButton";
import LightbulbIcon from "assets/icons/LightbulbIcon";

import styles from "./style.module.scss";

export interface GuideStep {
  title: string;
  body: ReactNode;
  tip?: ReactNode;
  art: ReactNode;
}

interface GuideProps {
  open: boolean;
  onClose: () => void;
  steps: GuideStep[];
  size?: "default" | "wide";
}

const Guide = ({ open, onClose, steps, size = "default" }: GuideProps) => {
  const [index, setIndex] = useState(0);
  const step = steps[index];
  const isFirst = index === 0;
  const isLast = index === steps.length - 1;

  const close = () => {
    setIndex(0);
    onClose();
  };

  if (!step) return null;

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      centered
      width={size === "wide" ? 880 : 680}
      destroyOnClose
      className={classNames(styles.guideModal, {
        [styles.wide]: size === "wide",
      })}
    >
      <div className={styles.grid} data-testid="guide-modal">
        <div className={styles.main}>
          <div className={styles.content}>
            <div className={styles.stepCounter}>
              Step {index + 1} of {steps.length}
            </div>
            <h2 key={`t-${index}`} className={styles.title}>
              {step.title}
            </h2>
            <div key={`b-${index}`} className={styles.body}>
              {step.body}
            </div>
            {step.tip && (
              <div key={`tip-${index}`} className={styles.tip}>
                <span className={styles.tipIcon}>
                  <LightbulbIcon />
                </span>
                <span>{step.tip}</span>
              </div>
            )}
          </div>
          <div className={styles.footer}>
            <div className={styles.dots}>
              {steps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to step ${i + 1}`}
                  aria-current={i === index}
                  className={classNames(styles.dot, {
                    [styles.dotActive]: i === index,
                  })}
                  onClick={() => setIndex(i)}
                />
              ))}
            </div>
            <div className={styles.buttons}>
              <VLButton
                type="default"
                size="middle"
                className={styles.footerButton}
                text={isFirst ? "Skip" : "Back"}
                onClick={isFirst ? close : () => setIndex(index - 1)}
              />
              <VLButton
                type="primary"
                size="middle"
                className={styles.footerButton}
                text={isLast ? "Got it" : "Next"}
                onClick={isLast ? close : () => setIndex(index + 1)}
              />
            </div>
          </div>
        </div>
        <div className={styles.hero} aria-hidden="true">
          <div key={`art-${index}`} className={styles.art}>
            {step.art}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default Guide;
